import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api-client";
import { DEFAULT_PREFERENCES, type OperatorPreferences } from "@capture/shared";

// Wave F /simplify — the type + defaults live in @capture/shared
// (packages/shared/src/types/preferences.ts), the single authoritative copy
// shared with the api route's GET-merge defaults / PUT allowlist. Re-exported
// here so existing `@/hooks/usePreferences` importers keep working.
export { DEFAULT_PREFERENCES };
export type { OperatorPreferences };

const STORAGE_KEY = "capture_operator_preferences";

function loadFromStorage(): OperatorPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<OperatorPreferences>;
      return { ...DEFAULT_PREFERENCES, ...parsed };
    }
  } catch {
    // Corrupt localStorage — fall through to defaults
  }
  return { ...DEFAULT_PREFERENCES };
}

function saveToStorage(prefs: OperatorPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage full or unavailable — ignore
  }
}

export function usePreferences() {
  const [preferences, setPreferences] = useState<OperatorPreferences>(loadFromStorage);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch from API on mount, merge over localStorage cache
  useEffect(() => {
    api
      .get<OperatorPreferences>("/preferences")
      .then((remote) => {
        const merged = { ...DEFAULT_PREFERENCES, ...remote };
        setPreferences(merged);
        saveToStorage(merged);
      })
      .catch(() => {
        // Use localStorage cache on failure
      })
      .finally(() => setIsLoading(false));
  }, []);

  // Update a single preference key — optimistic localStorage + debounced API save
  const updatePreference = useCallback(
    <K extends keyof OperatorPreferences>(key: K, value: OperatorPreferences[K]) => {
      setPreferences((prev) => {
        const next = { ...prev, [key]: value };
        saveToStorage(next);

        // Debounce API call (300ms)
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
          setIsSaving(true);
          api
            .put<OperatorPreferences>("/preferences", next)
            .then((saved) => {
              // Server returns merged defaults — update in case of new keys
              const merged = { ...DEFAULT_PREFERENCES, ...saved };
              setPreferences(merged);
              saveToStorage(merged);
            })
            .catch(() => {
              // Optimistic update stays — user sees their change
            })
            .finally(() => setIsSaving(false));
        }, 300);

        return next;
      });
    },
    [],
  );

  // Bulk save (for a "Save" button approach)
  const saveAll = useCallback(
    async (prefs: OperatorPreferences) => {
      setIsSaving(true);
      saveToStorage(prefs);
      setPreferences(prefs);
      try {
        const saved = await api.put<OperatorPreferences>("/preferences", prefs);
        const merged = { ...DEFAULT_PREFERENCES, ...saved };
        setPreferences(merged);
        saveToStorage(merged);
      } finally {
        setIsSaving(false);
      }
    },
    [],
  );

  return { preferences, isLoading, isSaving, updatePreference, saveAll };
}
